/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { sendEvent } from '@services/ga/send-event';
import { createExport } from '../api/spreadsheets.api';
import type { ExportFormat, ExportResult, SpreadsheetEntity } from '../types';

/**
 * Hands the browser a finished export.
 *
 * A blob is saved through an object URL.
 *
 * @param result - What the export endpoint answered.
 */
function saveExport(result: ExportResult): void {
  if (!result.blob || !result.file_name) return;
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.file_name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface UseSpreadsheetExport {
  isExporting: boolean;
  exportAs: (format: ExportFormat) => Promise<void>;
}

/**
 * Exports the listing as it currently stands -- same filters, same columns.
 *
 * @param agencyId - The agency the listing belongs to.
 * @param entity - Which listing is being exported.
 * @param filters - The active filters, straight from the URL.
 * @param columns - The visible column keys.
 * @returns The exporting flag and the one action the menu calls.
 */
export function useSpreadsheetExport(
  agencyId: string,
  entity: SpreadsheetEntity,
  filters: Record<string, unknown>,
  columns: string[],
): UseSpreadsheetExport {
  const { t } = useTranslation('features/spreadsheets');
  const [isExporting, setIsExporting] = useState(false);

  const exportAs = useCallback(async (format: ExportFormat) => {
    setIsExporting(true);
    const toastId = toast.loading(t('menu.exportStarted'));
    try {
      const result = await createExport(agencyId, { entity, format, filters, columns });
      saveExport(result);
      /*
       * A capped export is not a success. The file opens, the rows look
       * right, and the records past the ceiling are missing with nothing
       * on screen to say so -- which is how somebody works from a list
       * that is short by five thousand people. Both numbers are named,
       * because "exportamos 20.000 de 25.000" tells them what to do next
       * and "pronto" does not.
       */
      if (result.truncated) {
        toast.warning(t('menu.exportTruncated', {
          exported: result.exported, total: result.total,
        }), { id: toastId, duration: 10000 });
      } else {
        toast.success(t('menu.exportReady'), { id: toastId });
      }
      sendEvent('spreadsheet_export', { entity, format });
    } catch {
      toast.error(t('menu.exportFailed'), { id: toastId });
    } finally {
      setIsExporting(false);
    }
  }, [agencyId, entity, filters, columns, t]);

  return { isExporting, exportAs };
}
